// src/components/ShippingRecord/AddDisputePopin/context/DisputeFormContext.tsx
import { FormProvider, UseFormReturn } from "react-hook-form";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { EditMode, useDisputeFormLogic } from "../hooks/useDisputeFormLogic";
import {
  ShippingRecord,
  ShippingRecordDispute,
  Media,
  ShippingRecordDisputeType,
} from "$types";
import { FormValues } from "..";

type ShippingRecordDisputeOption = {
  label: string;
  value: string;
};

type DisputeFormContextType = {
  isOpen: boolean;
  editMode: EditMode;
  type: ShippingRecordDisputeType | null;
  linkedEntityId: string | null;
  media: Media[];
  // Update these type definitions to match what the hook returns
  opsWithNoDispute: ShippingRecordDisputeOption[];
  deliveryLocationsWithNoDispute: ShippingRecordDisputeOption[];
  shippingRecordDisputeTypeOptions: ShippingRecordDisputeOption[];
  isFetching: boolean;
  dispute?: ShippingRecordDispute;
  handleDisputeClose: () => Promise<void>;
  isDeleteModalOpen: boolean;
  toggleDeleteModal: (value: boolean) => void;
  createShippingRecordDispute: (data: any) => Promise<void>;
  updateShippingRecordDispute: (id: string, data: any) => Promise<void>;
  shippingRecord: ShippingRecord;
  selectedDispute?: ShippingRecordDispute;
  handleClose: () => void;
  form: UseFormReturn<FormValues>;
};

export const DisputeFormContext = createContext<
  DisputeFormContextType | undefined
>(undefined);

export const useDisputeForm = () => {
  const context = useContext(DisputeFormContext);
  if (context === undefined) {
    throw new Error("useDisputeForm must be used within DisputeFormProvider");
  }
  return context;
};

type DisputeFormProviderProps = {
  children: React.ReactNode;
  shippingRecord: ShippingRecord;
  selectedDispute?: ShippingRecordDispute;
  shippingRecordDisputes: ShippingRecordDispute[];
  isOpen: boolean;
  onClose: () => void;
};

export const DisputeFormProvider: React.FC<DisputeFormProviderProps> = ({
  children,
  shippingRecord,
  selectedDispute,
  shippingRecordDisputes = [],
  isOpen,
  onClose,
}) => {
  const [isDeleteModalOpen, toggleDeleteModal] = useState<boolean>(false);

  const {
    form,
    editMode,
    type,
    linkedEntityId,
    media,
    opsWithNoDispute,
    deliveryLocationsWithNoDispute,
    shippingRecordDisputeTypeOptions,
    isFetching,
    dispute,
    handleDisputeClose,
    createShippingRecordDispute,
    updateShippingRecordDispute,
  } = useDisputeFormLogic(
    shippingRecord,
    selectedDispute,
    shippingRecordDisputes
  );

  const handleClose = useCallback(() => {
    toggleDeleteModal(false);
    form.reset();
    onClose();
  }, [form, onClose]);

  // handleDisputeClose (du hook) ne ferme ni la modale de confirmation ni
  // le panneau du litige après succès. handleFormSubmit (création/édition,
  // plus bas) suit déjà le pattern onSubmit()+onClose() pour fermer le
  // panneau et déclencher le refresh du parent (fetchShippingRecord) : on
  // aligne la clôture d'un litige sur ce même pattern.
  const handleDisputeCloseAndCloseModal = useCallback(async () => {
    await handleDisputeClose();
    handleClose();
  }, [handleDisputeClose, handleClose]);

  const value: DisputeFormContextType = useMemo(
    () => ({
      isOpen,
      editMode,
      type,
      linkedEntityId,
      media,
      opsWithNoDispute,
      deliveryLocationsWithNoDispute,
      shippingRecordDisputeTypeOptions,
      isFetching,
      dispute,
      handleDisputeClose: handleDisputeCloseAndCloseModal,
      isDeleteModalOpen,
      toggleDeleteModal,
      createShippingRecordDispute,
      updateShippingRecordDispute,
      shippingRecord,
      selectedDispute,
      handleClose,
      form,
    }),
    [
      isOpen,
      editMode,
      type,
      linkedEntityId,
      media,
      opsWithNoDispute,
      deliveryLocationsWithNoDispute,
      shippingRecordDisputeTypeOptions,
      isFetching,
      dispute,
      handleDisputeCloseAndCloseModal,
      isDeleteModalOpen,
      createShippingRecordDispute,
      updateShippingRecordDispute,
      shippingRecord,
      selectedDispute,
      handleClose,
      form,
    ]
  );

  return (
    <DisputeFormContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </DisputeFormContext.Provider>
  );
};
